// SidebarButton.jsx

import React, { useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './SidebarButton.css';

// Icons
import Icon from 'svg/Icon';

/**
 * SidebarButton
 * 
 * Renders a button in the sidebar.
 * 
 * @param {string} iconKey - The key for the icon to display.
 * @param {string} label - The label to display on the button.
 * @param {ReactNode} rightContent - Optional right-side content to display.
 * @param {boolean} enabled - Whether the button is interactive.
 * @param {function} onClick - Handler for when the button is clicked.
 *  
 * @returns {ReactNode} - SidebarButton.
 */
const SidebarButton = React.memo(({ iconKey, label, rightContent, enabled = true, onClick }) => {
    
    // ----- CONTEXT ----- //

    // Theme Context
    const { theme } = useTheme();

    // Hover State
    const [hovered, setHovered] = useState(false);
    const [rightHovered, setRightHovered] = useState(false);

    // ----- HANDLERS ----- //

    /**
     * Handles click interaction when the button is enabled.
     */
    const handleClick = () => {
        if (!enabled) return;
        onClick && onClick();
    };

    return (

        <div className="sidebar-button-container"
            style={{
                backgroundColor: hovered && enabled ? theme.buttonHoverBackgroundColor : 'transparent',
                color: hovered && enabled ? theme.buttonHoverLabelColor : theme.foreground,
                cursor: enabled ? 'pointer' : 'not-allowed',
                opacity: enabled ? 1 : 0.45,
            }}
            onClick={handleClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => {
                setHovered(false);
                setRightHovered(false);
            }}
        >
            {/* ICON */}
            <div className="sidebar-button-icon">
                <Icon
                    iconKey={iconKey}
                    width={18}
                    height={18}
                    color={hovered && enabled ? theme.buttonHoverLabelColor : theme.foregroundMuted}
                />
            </div>

            {/* LABEL */}
            <div className="sidebar-button-label">
                {label}
            </div>

            {/* RIGHT CONTENT */}
            {rightContent && (
                <div
                    className="sidebar-button-right"
                    style={{
                        backgroundColor: rightHovered ? theme.backgroundHover : 'transparent',
                    }}
                    onMouseEnter={() => setRightHovered(true)}
                    onMouseLeave={() => setRightHovered(false)}
                >
                    {rightContent}
                </div>
            )}
        </div>
    );

});

// Dev Tools
SidebarButton.displayName = 'SidebarButton';

export default SidebarButton;

